import {filter, map, startWith, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {Budget, BudgetSummary, Material, ResumeMaterial} from '../../interfaces';
import {DataService} from '../../services/data.service';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {unities} from '../../interfaces/unit';
import {ProjectNrPipe} from '../../shared/pipes/project-nr.pipe';
import {MatDialog} from '@angular/material/dialog';
import {calculateOrderMaterialItemValue} from './OrderAddUtils';
import {MaterialDialogComponent} from '../../budget/sub-budget/material-dialog.component';
import {OrderEntry} from '../../interfaces/order';
import {compareMaterial} from './OrderComparisonUtils';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatIcon} from '@angular/material/icon';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {AsyncPipe, CurrencyPipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIconAnchor, MatIconButton, MatMiniFabButton} from '@angular/material/button';
import {ConfirmDialogComponent} from '../../private/private.component';



@Component({
    selector: 'app-order-entry-control',
    templateUrl: 'order-entry.component.html',
    imports: [
        MatFormField,
        MatSelect,
        MatOption,
        MatOption,
        MatIcon,
        MatAutocomplete,
        MatAutocompleteTrigger,
        ReactiveFormsModule,
        MatInput,
        AsyncPipe,
        ProjectNrPipe,
        RouterLink,
        MatTooltip,
        MatMiniFabButton,
        CurrencyPipe,
        MatIconButton,
        MatLabel,
        MatIconAnchor
    ],
    styleUrls: ['./order-entry.component.scss']
})
export class OrderEntryComponent implements OnInit, OnDestroy {

    @Input() currency: string;
    @Input() index: number;
    @Output() removed: EventEmitter<number> = new EventEmitter<number>();

    private _item: UntypedFormGroup;
    public get item(): UntypedFormGroup {
        return this._item;
    }

    @Input()
    public set item(i: UntypedFormGroup) {
        this._item = i;
        this.total = this.calculateTotal(this._item.value);
        this.initItem();
    }

    public materials$: Observable<Partial<ResumeMaterial>[]>;
    public materials: Array<Material> = [];
    public unities = unities;
    public total = 0;
    public budgets: BehaviorSubject<Partial<Budget>[]> = new BehaviorSubject([]);
    public filteredBudgets: Observable<Partial<Budget>[]>;
    public selectedBudget: BehaviorSubject<Budget> = new BehaviorSubject(null);
    public atLeastOneInvoiceAssociated = false; // if this item is linked to any invoice and/or ddt
    public relativeBudgetArchived = false;

    private unsubscribe$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        public dialog: MatDialog,
        private projectNrPipe: ProjectNrPipe
    ) {
    }

    ngOnInit() {
        this.dataService.getNotArchivedBudgetList().pipe(
            map(budgets =>
                budgets.map(b => ({
                    id: b.id,
                    budgetNr: b.budgetNr,
                    project: b.project,
                    company: b.company ? b.company : undefined
                }))
            ),
            takeUntil(this.unsubscribe$)
        ).subscribe(this.budgets);

        this.materials$ = this.selectedBudget.pipe(
            filter(budget => !!budget),
            switchMap((budget) => {
                this.updateItemControlStatus(budget);
                return this.dataService.getSubBudgetResume(budget.id);
            }),
            map((summary: BudgetSummary) => summary.materials.sort((a, b) => (a.description || '').localeCompare(b.description || ''))),
            tap(materials => {
                this.materials = materials;
            }),
            takeUntil(this.unsubscribe$)
        );
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    private initItem() {
        const invoices = this.item.get('invoices')?.value;
        const ddt = this.item.get('ddt')?.value;
        this.atLeastOneInvoiceAssociated = (invoices && invoices.length > 0) || (ddt && ddt.length > 0);

        if (this.item.get('budget').value) {
            this.selectedBudget.next(this.item.get('budget').value);
        }

        this.filteredBudgets = this.item.get('budget').valueChanges.pipe(
            startWith(this.item.get('budget').value || ''),
            map(budget => (budget && typeof budget === 'object') ? budget.budgetNr : String(budget)),
            map(budgetNr => budgetNr ? this.filterBudget(budgetNr) : this.budgets.value.slice())
        );

        this.item.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => this.updateTotal());

        if (this.item.get('budget').value?.id) {
            this.dataService.getBudget(this.item.get('budget').value?.id)
                .pipe(take(1))
                .subscribe((budget: Budget) => {
                    this.updateItemControlStatus(budget);
                });
        }
    }

    private updateItemControlStatus(budget: Budget): void {
        if (!this._item) return;
        this.relativeBudgetArchived = budget?.status === 'archived';
    }

    private calculateTotal(orderEntry: OrderEntry): number {
        return calculateOrderMaterialItemValue(orderEntry.costEach, orderEntry.quantity, orderEntry.discount);
    }

    private filterBudget(val: string): Partial<Budget>[] {
        const lowerCaseVal = val.toLowerCase();
        return this.budgets.value.filter(option => {
            if (option.budgetNr) {
                const searchString = [
                    option.budgetNr,
                    option.project ? this.projectNrPipe.transform(option) : '',
                    option.company?.companyName || ''
                ].join(' ').toLowerCase();
                return searchString.includes(lowerCaseVal);
            } else {
                console.warn(`No budget number for ${option.id}`);
                return option.id.toLowerCase().startsWith(lowerCaseVal);
            }
        });
    }

    public compareMaterial = (mat1: any, mat2: any): boolean => {
        return compareMaterial(mat1, mat2);
    }

    public onMaterialSelected(m: ResumeMaterial): void {
        if (!m) {
            return;
        }
        console.log(m);
        // For commissions, we use final value, see: https://gitlab.wonder-sys.com/extravega/wonderbudget/-/work_items/243
        if (m.category === 'CMM') {
            const project = this.selectedBudget.value?.project;
            const hasFinalInvoiced = project?.finalInvoiced !== undefined && project?.finalInvoiced !== null;
            const commValue = hasFinalInvoiced ? project.finalInvoiced : project?.finalOfferOrder;
            console.log(m);

            if (typeof commValue === 'number' && !isNaN(commValue) && commValue > 0) {
                const sourceLabel = hasFinalInvoiced ? 'Final Invoiced' : 'Final Offer Order';

                let commissionPercentage = 0.07;
                let commissionPercentageForDisplay = '7%';

                if (m.description) {
                    try {
                        const matches = m.description.match(/(\d+)\s*%/);
                        if (matches && matches[1]) {
                            const parsedPercentage = parseInt(matches[1], 10);
                            if (!isNaN(parsedPercentage)) {
                                commissionPercentage = parsedPercentage / 100;
                                commissionPercentageForDisplay = `${parsedPercentage}%`;
                            }
                        }
                    } catch (e) {
                        console.error("Could not parse commission percentage from description:", m.description, e);
                    }
                }

                const calculatedCost = Math.round((commValue * commissionPercentage + Number.EPSILON) * 100) / 100;

                const formattedCommValue = this.formatCurrency(commValue);
                const formattedCalculatedCost = this.formatCurrency(calculatedCost);
                const formattedDefaultCost = this.formatCurrency(m.costEach || 0);

                const confirmMessage = `This material is a commission.\n\n` +
                    `Do you want to calculate the unit cost as ${commissionPercentageForDisplay} of the project's ${sourceLabel} (${formattedCommValue}), which equals ${formattedCalculatedCost}?\n\n` +
                    `• Click OK to set unit cost to ${formattedCalculatedCost} (quantity: 1).\n` +
                    `• Click Cancel to use the default material cost (${formattedDefaultCost}).`;

                const dialogData = {data: {confirmMessage}, width: '600px'};
                const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogData);

                dialogRef.afterClosed().subscribe(choice => {
                    if (choice?.confirm) {
                        this.item.patchValue({
                            costEach: calculatedCost,
                            quantity: 1,
                            amount: calculatedCost,
                            unit: m.unit
                        });
                    } else {
                        this.applyStandardMaterialValues(m);
                    }
                });
                return;
            }
        }

        this.applyStandardMaterialValues(m);
    }

    private applyStandardMaterialValues(m: ResumeMaterial): void {
        this.item.patchValue({
            costEach: m.costEach,
            quantity: m.isOnlyFinal ? m.finalQuantity : m.quantity,
            amount: m.amount || 0,
            unit: m.unit
        });
    }

    private formatCurrency(value: number): string {
        const currency = this.currency || 'EUR';
        try {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        } catch {
            return `${currency} ${value.toFixed(2)}`;
        }
    }

    public updateTotal() {
        this.total = this.calculateTotal(this.item.value);
    }

    public budgetDisplayFn = (budget: Budget): string => {
        if (!budget) {
            return null;
        }
        let ret = budget.project ? this.projectNrPipe.transform(budget) : budget.budgetNr;
        // just old version compatibility issue
        const companyName = budget.company?.companyName || (budget as any).prosperworks?.company_name;
        if (companyName) {
            ret += ` ${companyName}`;
        }
        return ret;
    }

    public budgetChange($event) {
        if (!$event.option.value.id) {
            return;
        }
        this.item.get('material').reset('');
        this.selectedBudget.next($event.option.value);
    }

    public emptyItem() {
        this.item.get('material').reset('');
        this.item.patchValue({
            costEach: 0,
            quantity: 0,
            discount: 0,
            unit: ''
        });
    }

    public addNewMaterial() {
        this.emptyItem();
        const dialogRef = this.dialog.open(MaterialDialogComponent, {
            width: '520px',
            data: {
                budgetId: this.selectedBudget.value,
                subBudgetId: '',
                type: '',
                material: null,
                forOrderBuild: true,
                assignPossibilities: this.materials
            }
        });

        dialogRef.afterClosed().subscribe((m: any) => {
            if (!m || !this.selectedBudget.value) return;
            delete m.id;
            this.item.get('material').patchValue({
                // TODO: Replace with a proper unique ID generation strategy, preferably on the backend.
                id: 'id' + (Math.random() * 1000000000).toString(10).slice(0, 8),
                description: m.description,
                category: m.category,
                costEach: m.costEach,
                quantity: 0,
                unit: m.unit,
                isOnlyFinal: true,
                referencedMaterialId: m.referencedMaterialId ? m.referencedMaterialId : ''
            });
            this.onMaterialSelected(m);
        });
    }

    public getRefItemDescr(id: string): string {
        const found = this.materials.find(m => m.id === id);
        return found?.description || '';
    }
}
